/** Display text for knowledge values: property candidates with their conditions, packs and dilutions. */
import { MISSING_VALUE, formatNumber, formatPercent } from "@/lib/format";
import type { DecimalString } from "@/api/types";
import type { PropertyCandidate } from "./api";

const NBSP = "\u00a0";

function withUnit(text: string, unit: string): string {
  return unit ? `${text}${NBSP}${unit}` : text;
}

function number(value: DecimalString): string {
  return formatNumber(value, { maxDecimals: 6 });
}

const BOUND_SYMBOLS: Record<string, string> = {
  ">": ">",
  "<": "<",
  ">=": "≥",
  "≥": "≥",
  "<=": "≤",
  "≤": "≤",
  "~": "≈",
  "≈": "≈",
  "ca.": "ca.",
};

/** "> " for a value the source published as "> 93 °C"; empty for an exact value. */
function boundPrefix(conditions: Record<string, unknown> | undefined): string {
  const symbol = typeof conditions?.bound === "string" ? BOUND_SYMBOLS[conditions.bound.trim()] : undefined;
  return symbol ? `${symbol}${NBSP}` : "";
}

/**
 * "199 °C", "> 93 °C", "0.858–0.867 g/mL", "≥ 2", or the text value without stray spaces before commas
 * ("medium, recommend…"); "—" when the candidate has nothing to show.
 */
export function propertyValueText(
  candidate: Pick<PropertyCandidate, "value_num" | "value_low" | "value_high" | "unit" | "value_text"> &
    Partial<Pick<PropertyCandidate, "conditions">>,
): string {
  const { value_num: value, value_low: low, value_high: high, unit } = candidate;
  if (value !== null) return withUnit(`${boundPrefix(candidate.conditions)}${number(value)}`, unit);
  if (low !== null && high !== null) return withUnit(`${number(low)}–${number(high)}`, unit);
  if (low !== null) return withUnit(`≥${NBSP}${number(low)}`, unit);
  if (high !== null) return withUnit(`≤${NBSP}${number(high)}`, unit);
  return candidate.value_text.trim().replace(/[ \t]+(?=[,;])/g, "") || MISSING_VALUE;
}

/** Price bands show the € signs and the range they stand for: "€€ (15–50 EUR/kg)". */
export function priceBandText(candidate: PropertyCandidate): string {
  const range =
    candidate.value_low !== null || candidate.value_high !== null
      ? propertyValueText({ ...candidate, value_text: "", value_num: null })
      : "";
  const signs = candidate.value_text.trim();
  if (signs && range && range !== MISSING_VALUE) return `${signs} (${range})`;
  return signs || range || MISSING_VALUE;
}

function asNumberText(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return formatNumber(String(value), { maxDecimals: 3 });
  if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
    return formatNumber(value, { maxDecimals: 3 });
  }
  return null;
}

const METHOD_LABELS: Record<string, string> = {
  TCC: "closed cup",
  TOC: "open cup",
  CC: "closed cup",
  OC: "open cup",
};

function textCondition(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** ScenTree publishes pressure as { value, unit }: "at 10 mmHg"; standard pressure says nothing. */
function publishedPressureText(pressure: unknown): string | null {
  if (typeof pressure !== "object" || pressure === null) return null;
  const { value, unit } = pressure as { value?: unknown; unit?: unknown };
  const amount = asNumberText(value);
  if (!amount || typeof unit !== "string") return null;
  if (amount === "760" && /^(mmhg|torr)$/i.test(unit)) return null;
  return `at ${amount}${NBSP}${unit}`;
}

/**
 * Measurement conditions in words: "at 50 mmHg (reduced pressure)", "at 25 °C", "in air", "at 10.00 % in
 * dipropylene glycol". Bounds belong to the value (propertyValueText); import notes and the unit as published
 * are provenance, not conditions.
 */
export function conditionsText(conditions: Record<string, unknown>): string[] {
  const parts: string[] = [];
  const pressure = asNumberText(conditions.pressure_mmhg);
  if (pressure && conditions.reduced_pressure) parts.push(`at ${pressure}${NBSP}mmHg (reduced pressure)`);
  else if (pressure && pressure !== "760") parts.push(`at ${pressure}${NBSP}mmHg`);
  const publishedPressure = publishedPressureText(conditions.pressure);
  if (publishedPressure) parts.push(publishedPressure);
  if (conditions.pressure_stated === false) parts.push("pressure not stated");
  const temperature = asNumberText(conditions.temp_c);
  if (temperature) parts.push(`at ${temperature}${NBSP}°C`);
  if (typeof conditions.basis === "string" && conditions.basis && conditions.basis !== "unspecified") {
    parts.push(conditions.basis === "air" ? "in air" : conditions.basis);
  }
  if (typeof conditions.method === "string" && conditions.method) {
    parts.push(METHOD_LABELS[conditions.method] ?? conditions.method);
  }
  const atPercent = asNumberText(conditions.at_percent);
  if (atPercent) parts.push(`at ${atPercent}${NBSP}%`);
  const dilution = textCondition(conditions.dilution);
  if (dilution) parts.push(dilution.replace(/\.$/, ""));
  const lineLabel = textCondition(conditions.label);
  if (lineLabel) parts.push(lineLabel);
  return parts;
}

/** How a value was obtained, worth saying next to it; measured, editorial and source-specific methods go unsaid. */
const NOTEWORTHY_METHODS: Record<string, string> = {
  estimated: "estimated",
  predicted: "predicted",
};

/**
 * Conditions plus how the value was obtained, for display after the value: ["at 14 mmHg (reduced pressure)"],
 * ["estimated"]. A temperature the property name already states ("Vapor pressure at 25 °C") is left out.
 */
export function conditionsAndMethodText(
  candidate: Pick<PropertyCandidate, "property" | "conditions" | "method">,
): string[] {
  const parts = conditionsText(
    candidate.property.endsWith("_25c") ? { ...candidate.conditions, temp_c: null } : candidate.conditions,
  );
  const method = NOTEWORTHY_METHODS[candidate.method];
  if (method) parts.push(method);
  return parts;
}

/** A note the import left on a value ("French text inside the value: 'et'"), shown with its provenance. */
export function importNoteText(conditions: Record<string, unknown>): string | null {
  return textCondition(conditions.note);
}

/** Who wrote an odor description, when the source names them. */
export function attributionText(conditions: Record<string, unknown>): string | null {
  return typeof conditions.attribution === "string" && conditions.attribution.trim()
    ? conditions.attribution.trim()
    : null;
}

/** "10 % in DPG", "10 %", or null for an undiluted pack. */
export function dilutionText(fraction: DecimalString | null, solvent?: string | null): string | null {
  if (fraction === null || fraction === "1") return null;
  const percent = formatPercent(fraction, { from: "fraction", maxDecimals: 2, minDecimals: 0 });
  return solvent?.trim() ? `${percent} in ${solvent.trim()}` : percent;
}

/** "5 g", "1 kg" for a pack size in grams. */
export function packText(packG: DecimalString | null): string {
  if (packG === null) return MISSING_VALUE;
  const grams = Number(packG);
  if (grams >= 1000 && Number.isInteger(grams / 1000)) return `${formatNumber(String(grams / 1000))}${NBSP}kg`;
  return `${formatNumber(packG, { maxDecimals: 3 })}${NBSP}g`;
}

/** "5 g", or "10 mL" for a pack sold by volume (no weight known). */
export function packSizeText(
  packG: DecimalString | null,
  packQty: DecimalString | null,
  packUnit: string | null,
): string {
  if (packG !== null || packUnit !== "ml" || packQty === null) return packText(packG);
  return `${formatNumber(packQty, { maxDecimals: 3 })}${NBSP}mL`;
}

const SCHEME_LABELS: Record<string, string> = {
  CAS: "CAS",
  EC: "EC",
  FEMA: "FEMA",
  JECFA: "JECFA",
  FLAVIS: "FLAVIS",
  COE: "CoE",
  PUBCHEM_CID: "PubChem CID",
  INCHIKEY: "InChIKey",
  UNII: "UNII",
  IFRA_STD: "IFRA standard",
  TGSC_ID: "TGSC",
  SCENTREE_ID: "ScenTree",
  SUPPLIER_CODE: "Supplier code",
  MANUFACTURER_CODE: "Manufacturer code",
};

export function schemeLabel(scheme: string): string {
  return SCHEME_LABELS[scheme] ?? scheme;
}

const RELATION_LABELS: Record<string, string> = {
  exact: "exact",
  stereo_unspecified: "stereo unspecified",
  racemate: "racemate",
  broader: "broader (covers every grade of the natural)",
  component_of: "component of",
  deprecated: "deprecated",
  erroneous: "known to be wrong",
  related: "related",
};

export function relationLabel(relation: string): string {
  return RELATION_LABELS[relation] ?? relation.replaceAll("_", " ");
}

const TRUST_LABELS: Record<string, string> = {
  authoritative: "Authoritative source",
  curated: "Curated reference",
  community: "Community source",
  marketing: "Supplier’s own claim",
  scraped: "Scraped, unverified",
  human: "Your own entry",
};

export function trustLabel(tier: string): string {
  return TRUST_LABELS[tier] ?? tier;
}
